// ─────────────────────────────────────────────────────────────────
// Client-side REST wrapper for /api/db/query.
//
// Every helper currently in src/db.js
// gets rewritten to call this API when we want to skip the chainable shim.
//
// Design:
//   * All requests go through authFetch — auto-attaches the
//     Keycloak Bearer token and refreshes if near expiry.
//   * Uniform response envelope: { data, error, count }
//   * Endpoints live under /api/ and match REST conventions:
//       GET    /api/<resource>?filter=...
//       GET    /api/<resource>/:id
//       POST   /api/<resource>
//       PATCH  /api/<resource>/:id
//       DELETE /api/<resource>/:id
//
// USAGE (in db.js call-site rewrites):
//   // Old:
//   const { data } = await db.from('opportunities')
//     .select('*').eq('org_id', orgId);
//
//   // New:
//   const { data } = await api.get('/api/opportunities', { org_id: orgId });
// ─────────────────────────────────────────────────────────────────

import { authFetch } from './auth.js';

async function parseResponse(res) {
  let body;
  try { body = await res.json(); }
  catch { body = { error: `HTTP ${res.status}` }; }
  if (!res.ok) return { data: null, error: body?.error || `HTTP ${res.status}` };
  // Two envelope conventions are in use across our endpoints:
  //   1. { data, error, count } — /api/db/query and most helpers
  //   2. bare object            — legacy / RPC-style handlers
  // Detect (1) by presence of the `data` key.  DO NOT use `body.data
  // ?? body` here — a legitimately-null `data` (maybeSingle with no
  // match) would then fall through to return the entire envelope
  // object, and the caller sees `{ data: null, count }` masquerading
  // as a matched row.  That's how the CRM "Open in Proposal Coach"
  // second-click shipped a toast reading `Opened "undefined" in
  // Proposal Coach` — the second click hit findOpportunityByCrmDealId,
  // no row matched, and this line handed the envelope back as if it
  // were a row.
  const hasWrapper = body && typeof body === 'object' && 'data' in body;
  return {
    data:  hasWrapper ? body.data : body,
    error: null,
    count: body?.count ?? null,
  };
}

/** Query params → URL string */
function qs(params) {
  if (!params) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach(item => usp.append(k, item));
    else usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  async get(path, params) {
    const res = await authFetch(`${path}${qs(params)}`, { method: 'GET' });
    return parseResponse(res);
  },
  async post(path, body) {
    const res = await authFetch(path, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body ?? {}),
    });
    return parseResponse(res);
  },
  async patch(path, body) {
    const res = await authFetch(path, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body ?? {}),
    });
    return parseResponse(res);
  },
  async delete(path) {
    const res = await authFetch(path, { method: 'DELETE' });
    return parseResponse(res);
  },
  // Escape hatch for RPCs / non-CRUD endpoints (AI calls, etc.)
  async rpc(path, body) {
    return this.post(path, body);
  },
};
